import { cx, styled } from "simplydo/react/styled";
import { useTheme, type UiColor } from "simplydo/react/theme";

import { Divider } from "../divider";
import { headingBaseClass } from "../header/Header.styles";
import { Icon } from "../icon";
import type { UiColorPalette } from "../utils/UIContext";
import { SIZE_FONT } from "../utils/colors";
import { resolveUiPalette } from "../utils/uiPalette";
import type { MessageProps, MessageSize } from "./Message.types";

type StyleProps = { $color: UiColorPalette; $size?: MessageSize };

const StyledMessage = styled.div<StyleProps>`
  position: relative;
  min-height: 1em;
  margin: 1em 0;
  background: ${({ $color }) => $color.softer};
  padding: 1em 1.25em;
  line-height: 1.4em;
  color: ${({ $color }) => $color.ink};
  transition: opacity 0.1s ease;
  border-radius: 0.28571429rem;
  box-shadow: 0 0 0 1px ${({ $color }) => $color.strong} inset;
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || "1em"};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  > .${headingBaseClass} {
    margin-top: 0;
  }

  ${Divider} {
    border-color: ${({ $color }) => $color.strong};
  }

  &:has(> ${Icon}:first-child) {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 0.75em;
    align-items: start;

    > *:not(${Icon}) {
      grid-column: 2;
    }

    > ${Icon} {
      grid-column: 1;
      grid-row: 1;
      margin-top: 0.15em;
    }
  }
`;

const MessageComponent = ({
  color,
  size,
  children,
  className,
  style,
}: MessageProps) => {
  const theme = useTheme();
  const palette = resolveUiPalette(theme, (color ?? "grey") as UiColor);

  return (
    <StyledMessage
      className={cx(className)}
      style={style}
      role="status"
      $color={palette}
      $size={size}
    >
      {children}
    </StyledMessage>
  );
};

export const Message = styled(MessageComponent)``;
